export type OrgKind = 'company' | 'agency' | 'provider';

export function isAgency(kind: OrgKind | null | undefined) {
  return kind === 'agency';
}

export function defaultMapPath() {
  return '/screening/maps';
}

// The agency and employer talent map labels are not confirmed yet.
const MAP_WORDS = {
  provider: {
    group: 'Cohort',
    groups: 'Cohorts',
    pool: 'Your pool',
    partner: 'Employer partner',
    outcome: 'Placed',
  },
  agency: {
    group: 'Campaign',
    groups: 'Campaigns',
    pool: 'Talent pool',
    partner: 'Client',
    outcome: 'Placed',
  },
  company: {
    group: 'Intake',
    groups: 'Intakes',
    pool: 'Talent pool',
    partner: 'Hiring team',
    outcome: 'Hired',
  },
};

export function mapWords(kind: OrgKind | null | undefined) {
  return MAP_WORDS[kind && kind in MAP_WORDS ? kind : 'company'];
}

export function orgCopy(kind: OrgKind | null | undefined) {
  if (kind === 'provider') {
    return {
      ...mapWords(kind),
      nav: 'Cohorts',
      eyebrow: 'COHORTS',
      title: 'Cohorts',
      emptyTitle: 'No cohorts yet',
      emptyBody:
        'Name a cohort and share one link. People join with a short conversation.',
      add: 'Add a cohort',
      invitePage: 'Invite a cohort',
      inviteTitle: 'Who is in this cohort?',
      inviteLead:
        'They get one link. The conversation is the first step. A CV can come after.',
      nameLabel: 'Cohort',
      leadLabel: 'Lead name',
      answerSelf: (on: boolean) =>
        on ? "You'll answer" : "I'll answer this cohort",
      addAnother: 'Add another cohort',
      answerAll: "I'll answer the cohorts myself",
      noContact: 'No lead yet',
      across: (count: number) =>
        `across ${count} ${count === 1 ? 'cohort' : 'cohorts'}`,
      stronger: 'Stronger in another cohort',
      strongerNote:
        'They finished a conversation in one cohort and fit another.',
      yours: ' (your cohort)',
      backAll: 'Back to all cohorts',
      backOne: 'Back to the cohort',
      selectReady: 'Select a cohort',
      field: 'Cohort',
      define: (name: string) => `Open ${name}`,
    };
  }
  const agency = kind === 'agency';
  return {
    ...mapWords(kind),
    nav: agency ? 'Clients' : 'Teams',
    eyebrow: agency ? 'CLIENTS' : 'TEAMS',
    title: agency ? 'Clients' : 'Teams',
    emptyTitle: agency ? 'No clients yet' : 'No teams yet',
    emptyBody: agency
      ? 'Name the clients you hire for and the person at each client who owns the search.'
      : 'Name the groups you hire into and invite the people who own those hires.',
    add: agency ? 'Add a client' : 'Add a team',
    invitePage: agency ? 'Add clients' : 'Invite team leads',
    inviteTitle: agency
      ? 'Who is the contact at each client?'
      : 'Who owns hiring on each team?',
    inviteLead: agency
      ? 'They get an email, create an account, and answer how this client hires. You keep the agency profile.'
      : 'They get an email, create an account, and answer the questions only they can answer. You keep the company profile.',
    nameLabel: agency ? 'Client' : 'Team',
    leadLabel: agency ? 'Contact name' : 'Lead name',
    answerSelf: (on: boolean) =>
      on
        ? "You'll answer"
        : agency
        ? "I'll answer this client"
        : "I'll answer this team",
    addAnother: agency ? 'Add another client' : 'Add another team',
    answerAll: agency
      ? "I'll answer the clients myself"
      : "I'll answer the teams myself",
    noContact: agency ? 'No contact yet' : 'No lead invited',
    across: (count: number) =>
      `across ${count} ${agency ? 'clients' : 'teams'}`,
    stronger: agency
      ? 'Stronger on another client'
      : 'Stronger on another team',
    strongerNote: agency
      ? 'They applied to one client and score higher on another.'
      : 'They applied to one team and score higher on another.',
    yours: agency ? ' (your client)' : ' (your team)',
    backAll: agency ? 'Back to all clients' : 'Back to all teams',
    backOne: agency ? 'Back to the client' : 'Back to the team',
    selectReady: agency ? 'Select a ready client' : 'Select a ready team',
    field: agency ? 'Client' : 'Team',
    define: (name: string) => `Define how ${name} hires`,
  };
}
